import React from "react";
import { Badge } from "@musa.tech/design-system";
import { ChevronDown, Lock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ADMIN_PROFILE_KEY,
  OFFERED_PERMISSION_PROFILES,
  PERMISSION_PROFILES,
  PERMISSION_PROFILE_SECTIONS,
  getPermissionProfileLabel,
  isOfferedProfile,
  isMandatoryProfile,
  isProfileFullyPlanned,
  toggleProfile,
  withMandatoryProfiles,
  type PermissionProfile,
} from "@/constants/permissionProfiles";
import { cn } from "@/lib/utils";

export interface PermissionProfilesSelectProps {
  /** Chaves dos perfis marcados. Componente controlado. */
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
  /**
   * Perfis atualmente gravados no servidor (modo edição). Quando presente, a
   * tela mostra o que a gravação vai mudar — badge "novo" no que entra,
   * "será removido" no que sai, e o resumo do diff. O PUT é add/remove: o
   * usuário precisa ver o diff, não adivinhar.
   */
  baseline?: string[];
}

interface ProfileRowProps {
  profile: PermissionProfile;
  checked: boolean;
  disabled: boolean;
  /**
   * Perfil obrigatório: marcado e sem como desmarcar. Diferente de `disabled`,
   * que é o formulário inteiro travado — aqui a trava é permanente e precisa
   * ser lida como decisão, não como indisponibilidade.
   */
  locked?: boolean;
  /** null = sem baseline (criação): não há diff a mostrar. */
  change: "add" | "remove" | null;
  onToggle: (checked: boolean) => void;
}

const ProfileRow: React.FC<ProfileRowProps> = ({
  profile,
  checked,
  disabled,
  locked = false,
  change,
  onToggle,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const Icon = profile.icon;
  const checkboxId = `permission-profile-${profile.key}`;
  const summaryId = `${checkboxId}-summary`;
  const fullyPlanned = isProfileFullyPlanned(profile);
  const capabilityCount = profile.capabilities.length;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      // `border-primary`/`bg-primary` sem degrau NÃO servem: no preset do DS
      // `primary.DEFAULT` é `var(--background)`, não o azul da marca. E o
      // preset não declara `<alpha-value>`, então `/5` e afins são ignorados.
      className={cn(
        "rounded-lg border transition-colors",
        checked ? "border-primary-500 bg-primary-50" : "border-input bg-card",
        change === "remove" && "border-error-400",
      )}
    >
      <div className="flex items-start gap-3 p-3">
        {/* O Checkbox fica FORA do CollapsibleTrigger de propósito: o trigger é
            um <button>, e aninhar controle dentro dele quebra o HTML e o
            teclado. Expandir e marcar são ações separadas. */}
        <Checkbox
          id={checkboxId}
          checked={checked}
          disabled={disabled || locked}
          onCheckedChange={(state) => onToggle(state === true)}
          aria-describedby={summaryId}
          className="mt-0.5"
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <label
              htmlFor={checkboxId}
              className={cn(
                "flex items-center gap-2 text-sm font-medium",
                locked && "cursor-default",
                disabled && !locked && "cursor-not-allowed opacity-70",
                !disabled && !locked && "cursor-pointer",
              )}
            >
              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span>{profile.label}</span>
            </label>

            {locked && (
              <Badge variant="secondary" size="small">
                Obrigatório
              </Badge>
            )}
            {profile.key === ADMIN_PROFILE_KEY && (
              <Badge variant="warning" size="small">
                gestão de usuários
              </Badge>
            )}
            {/* Perfil sem NENHUMA tela entregue: sinalizar no card, não só
                dentro da lista expandida — senão o admin concede e a pessoa
                fica procurando uma tela que não existe. */}
            {fullyPlanned && (
              <Badge variant="secondary" size="small">
                Em breve
              </Badge>
            )}
            {change === "add" && (
              <Badge variant="success" size="small">
                novo
              </Badge>
            )}
            {change === "remove" && (
              <Badge variant="destructive" size="small">
                será removido
              </Badge>
            )}
          </div>
          <p id={summaryId} className="mt-0.5 text-xs text-muted-foreground">
            {profile.summary}
            {locked && " Todo usuário recebe este perfil — não dá para remover."}
          </p>
        </div>

        <CollapsibleTrigger
          type="button"
          className="flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-neutral-100 hover:text-foreground"
        >
          {/* Nome acessível = texto visível + contexto oculto, nessa ordem —
              controle por voz ("clicar 10 permissões") precisa achar a string
              visível dentro do nome (WCAG 2.5.3). Um aria-label que reordena
              ou reescreve o texto quebra isso. */}
          {isOpen
            ? "Ocultar"
            : `${capabilityCount} ${capabilityCount === 1 ? "permissão" : "permissões"}`}
          <span className="sr-only"> — perfil {profile.label}</span>
          <ChevronDown
            className={cn("h-3.5 w-3.5 transition-transform", isOpen && "rotate-180")}
          />
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <ul className="space-y-1.5 border-t border-input px-3 py-3 pl-10">
          {profile.capabilities.map((capability, index) => (
            <li
              key={`${capability.area}-${index}`}
              className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1 text-xs"
            >
              <span className="font-medium text-foreground">{capability.area}</span>
              <span className="text-muted-foreground">— {capability.action}</span>
              {capability.planned && (
                <Badge variant="secondary" size="small">
                  Em breve
                </Badge>
              )}
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
};

/**
 * Seleção dos perfis de acesso do usuário — GR-637.
 *
 * Substitui o antigo par "Operacional / Gestor Admin": os perfis somam, não se
 * excluem. A tela responde três perguntas na ordem em que o admin as faz:
 * o que cada perfil libera (card + lista expandível), o que a pessoa vai poder
 * fazer no total (resumo do acesso) e, na edição, o que o Salvar vai mudar
 * (badges de diff e a linha "Ao salvar").
 */
export const PermissionProfilesSelect: React.FC<PermissionProfilesSelectProps> = ({
  value,
  onChange,
  disabled = false,
  isLoading = false,
  baseline,
}) => {
  // Seleção EFETIVA: o obrigatório conta como marcado mesmo que o pai não o
  // tenha mandado. Card, resumo e diff passam a falar do mesmo conjunto que a
  // gravação vai aplicar (`diffProfiles` normaliza igual) — sem isso a tela
  // mostraria "Default desmarcado" e o PUT gravaria marcado.
  const selected = React.useMemo(
    () => new Set(withMandatoryProfiles(value)),
    [value],
  );
  const baselineSet = React.useMemo(
    () => (baseline ? new Set(baseline) : null),
    [baseline],
  );

  // Tudo o que o usuário tem e NÃO aparece como checkbox acima. São dois casos,
  // e os dois são acesso real — esconder daria a impressão falsa de que a lista
  // acima é tudo o que a pessoa tem:
  //
  //   - agrupamento por gerador criado antes do GR-637 (`Generator_3869`), que
  //     não está no catálogo;
  //   - perfil do catálogo que hoje não é ofertado porque nenhuma capacidade
  //     dele tem tela (`legal`, `data`).
  //
  // O segundo caso é o motivo de o critério ser "não ofertado" em vez de "não
  // está no catálogo": com o filtro por `planned`, `legal` continua no catálogo
  // e ficaria invisível nas duas listas.
  //
  // `getPermissionProfileLabel` resolve os dois: rótulo do catálogo quando
  // existe, nome cru quando não.
  const readOnlyProfiles = React.useMemo(
    () => value.filter((key) => !isOfferedProfile(key)),
    [value],
  );

  const changeFor = (key: string): "add" | "remove" | null => {
    if (!baselineSet) return null;
    if (selected.has(key) && !baselineSet.has(key)) return "add";
    if (!selected.has(key) && baselineSet.has(key)) return "remove";
    return null;
  };

  // Resumo do acesso: áreas únicas liberadas HOJE pelos perfis marcados, na
  // ordem do catálogo, mais a contagem do que só existirá depois. É a resposta
  // consolidada a "o que essa pessoa vai poder fazer?" — sem obrigar o admin a
  // expandir card por card e somar de cabeça.
  const summary = React.useMemo(() => {
    const areas: string[] = [];
    const seen = new Set<string>();
    let plannedCount = 0;
    for (const profile of OFFERED_PERMISSION_PROFILES) {
      if (!selected.has(profile.key)) continue;
      for (const capability of profile.capabilities) {
        if (capability.planned) {
          plannedCount += 1;
          continue;
        }
        if (!seen.has(capability.area)) {
          seen.add(capability.area);
          areas.push(capability.area);
        }
      }
    }
    return { areas, plannedCount };
  }, [selected]);

  const diff = React.useMemo(() => {
    if (!baselineSet) return null;
    const added = PERMISSION_PROFILES.filter(
      (profile) => selected.has(profile.key) && !baselineSet.has(profile.key),
    ).map((profile) => profile.key);
    const removed = PERMISSION_PROFILES.filter(
      (profile) => !selected.has(profile.key) && baselineSet.has(profile.key),
    ).map((profile) => profile.key);
    return added.length || removed.length ? { added, removed } : null;
  }, [baselineSet, selected]);

  const handleToggle = (key: string, checked: boolean) => {
    // A regra de quem pode ser desmarcado vive em `toggleProfile`, no catálogo.
    const next = toggleProfile(value, key, checked);
    if (next !== value) onChange(next);
  };

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="permission-profiles-loading">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-[68px] w-full rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {PERMISSION_PROFILE_SECTIONS.map((section) => {
        const profiles = OFFERED_PERMISSION_PROFILES.filter(
          (profile) => profile.section === section,
        );
        if (profiles.length === 0) return null;
        return (
          <div key={section} className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {section}
            </p>
            {profiles.map((profile) => (
              <ProfileRow
                key={profile.key}
                profile={profile}
                checked={selected.has(profile.key)}
                disabled={disabled}
                locked={isMandatoryProfile(profile.key)}
                change={changeFor(profile.key)}
                onToggle={(checked) => handleToggle(profile.key, checked)}
              />
            ))}
          </div>
        );
      })}

      {readOnlyProfiles.length > 0 && (
        <div className="rounded-lg border border-input bg-neutral-100 p-3">
          <div className="flex items-start gap-2">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Outros acessos</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Mantidos como estão — grupos criados antes dos perfis de acesso,
                ou perfis cuja tela ainda não foi entregue. Para remover, fale
                com a equipe Musa: a tela não desfaz o que não consegue refazer.
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {readOnlyProfiles.map((key) => (
                  <Badge key={key} variant="outline" size="small">
                    {getPermissionProfileLabel(key)}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="rounded-lg border border-input bg-neutral-100 p-3"
        data-testid="permission-profiles-summary"
      >
        <p className="text-sm font-medium">Resumo do acesso</p>
        {/* Sempre há área a listar: `default` é obrigatório e entrega tela de
            verdade. Não existe mais o caso "nenhuma tela liberada". */}
        <p className="mt-0.5 text-xs text-muted-foreground">
          Com os perfis marcados, a pessoa acessa:
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {summary.areas.map((area) => (
            <Badge key={area} variant="outline" size="small">
              {area}
            </Badge>
          ))}
        </div>
        {summary.plannedCount > 0 && (
          <p className="mt-2 text-xs text-muted-foreground">
            + {summary.plannedCount}{" "}
            {summary.plannedCount === 1
              ? "permissão entra em vigor"
              : "permissões entram em vigor"}{" "}
            quando as telas planejadas forem lançadas.
          </p>
        )}
        {diff && (
          <p className="mt-2 border-t border-input pt-2 text-xs">
            <span className="font-medium">Ao salvar:</span>{" "}
            {diff.added.length > 0 && (
              <span className="text-success-700">
                adiciona {diff.added.map(getPermissionProfileLabel).join(", ")}
              </span>
            )}
            {diff.added.length > 0 && diff.removed.length > 0 && (
              <span className="text-muted-foreground"> · </span>
            )}
            {diff.removed.length > 0 && (
              <span className="text-error-600">
                remove {diff.removed.map(getPermissionProfileLabel).join(", ")}
              </span>
            )}
          </p>
        )}
      </div>
    </div>
  );
};
